import { z } from 'zod';

/* ─────────────────────────────────────────────────────────────────────
   Strategy — as 10 ferramentas.
   Cada uma tem um conteúdo com formato próprio (tabela, canvas, matriz),
   guardado em JSONB e validado aqui. O progresso é derivado do conteúdo,
   nunca digitado: a régua mostra o que de fato foi preenchido.
   ───────────────────────────────────────────────────────────────────── */

const str = (max = 400) => z.string().trim().max(max).default('');
const filled = (s: unknown) => typeof s === 'string' ? s.trim().length > 0 : s != null;
const ratio = (n: number, d: number) => d <= 0 ? 0 : Math.max(0, Math.min(1, n / d));

/* ── 01 · Mapa de Gaps ─────────────────────────────────────────────── */
export const gapIndicatorSchema = z.object({
  /** identidade estável: as evidências (arquivos) se ligam a ela */
  id:       z.string().regex(/^[a-z0-9][a-z0-9-]{1,47}$/).optional(),
  name:     str(120),
  /** o que o indicador mede — a dica do "?" */
  description: str(240),
  what:     str(),
  why:      str(),
  where:    str(120),
  who:      str(120),
  when:     z.union([z.iso.date(), z.literal('')]).default(''),
  how:      str(),
  cost:     z.number().nonnegative().nullable().default(null),
  /** quantos arquivos de evidência estão ligados ao indicador (o servidor conta; não é digitado) */
  evidence: z.number().int().nonnegative().default(0),
  maturity: z.number().int().min(0).max(5).default(0),
  target:   z.number().int().min(0).max(5).default(0),
});
export const gapFactorSchema = z.object({
  key:  z.string().min(1).max(40),
  name: str(120),
  indicators: z.array(gapIndicatorSchema).max(20).default([]),
});
export const gapsSchema = z.object({
  factors: z.array(gapFactorSchema).max(8).default([]),
});
export type GapIndicator = z.infer<typeof gapIndicatorSchema>;
export type GapFactor    = z.infer<typeof gapFactorSchema>;
export type Gaps         = z.infer<typeof gapsSchema>;

/** Um indicador está completo quando as 8 colunas descritivas e a maturidade têm valor. */
const GAP_FIELDS = ['what', 'why', 'where', 'who', 'when', 'how'] as const;
export function gapIndicatorProgress(i: GapIndicator): number {
  const parts = [...GAP_FIELDS.map(f => filled(i[f])), i.cost != null, i.evidence > 0, i.maturity > 0];
  return ratio(parts.filter(Boolean).length, parts.length);
}
export function gapFactorProgress(f: GapFactor): number {
  if (!f.indicators.length) return 0;
  return f.indicators.reduce((s, i) => s + gapIndicatorProgress(i), 0) / f.indicators.length;
}
/** Cobertura de evidências: fatia dos indicadores com ao menos um arquivo. */
export function gapEvidenceCoverage(f: GapFactor): number {
  return ratio(f.indicators.filter(i => i.evidence > 0).length, f.indicators.length);
}

/** Indicadores antigos (sem id) ganham um id estável: fator + posição. */
export function withGapIds(g: Gaps): Gaps {
  const seen = new Set<string>();
  return {
    factors: g.factors.map(f => ({
      ...f,
      indicators: f.indicators.map((i, n) => {
        let id = i.id ?? `${f.key.toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/^-+/, '').slice(0, 30) || 'f'}-${n + 1}`;
        while (seen.has(id)) id = `${id}-b`;
        seen.add(id);
        return { ...i, id };
      }),
    })),
  };
}

/* ── 02 · Diferenciais Competitivos ───────────────────────────────── */
/** A trinca: cada diferencial recebe três notas de 1 a 5. */
export const DIFF_CRITERIA = [
  { key: 'real',     label: 'A organização realmente tem.',   short: 'Realidade' },
  { key: 'distinct', label: 'Outras não têm nesse nível.',    short: 'Distinção' },
  { key: 'hard',     label: 'E isso é difícil de copiar.',    short: 'Dificuldade de cópia' },
] as const;
export type DiffCriterion = (typeof DIFF_CRITERIA)[number]['key'];
const nota = z.number().int().min(0).max(5).default(0);
export const diferencialSchema = z.object({
  /** identidade estável: as evidências (arquivos) se ligam a ela */
  id:          z.string().regex(/^[a-z0-9][a-z0-9-]{1,47}$/).optional(),
  label:       str(160),
  description: str(),
  /** a trinca, de 1 a 5 (0 = sem nota) */
  real:        nota,
  distinct:    nota,
  hard:        nota,
  /** força de 0 a 100, derivada da trinca (a soma sobre 15) */
  strength:    z.number().int().min(0).max(100).default(0),
  /** uma frase de evidência, em texto (os arquivos contam à parte) */
  evidence:    str(),
  /** quantos arquivos de evidência estão ligados ao diferencial (o servidor conta) */
  files:       z.number().int().nonnegative().default(0),
});
export const diferenciaisSchema = z.object({
  items: z.array(diferencialSchema).max(12).default([]),
});
export type Diferencial = z.infer<typeof diferencialSchema>;
export type Diferenciais = z.infer<typeof diferenciaisSchema>;
export const diffSum = (d: Pick<Diferencial, DiffCriterion>) => d.real + d.distinct + d.hard;
export const diffRated = (d: Pick<Diferencial, DiffCriterion>) => d.real > 0 && d.distinct > 0 && d.hard > 0;

/**
 * Ids estáveis e força derivada. Diferenciais antigos, só com a força de
 * 0 a 100, ganham uma trinca equivalente.
 */
export function withDiffDerived(c: Diferenciais): Diferenciais {
  const seen = new Set<string>();
  return {
    items: c.items.map((d, n) => {
      let id = d.id ?? `dif-${n + 1}`;
      while (seen.has(id)) id = `${id}-b`;
      seen.add(id);
      let { real, distinct, hard } = d;
      if (!real && !distinct && !hard && d.strength > 0) {
        const s = Math.max(1, Math.min(5, Math.round(d.strength / 20)));
        real = distinct = hard = s;
      }
      const sum = real + distinct + hard;
      return { ...d, id, real, distinct, hard, strength: sum ? Math.round((sum / 15) * 100) : 0 };
    }),
  };
}

/* ── 03 · Tríade de Valor ─────────────────────────────────────────── */
/** O método: "Acreditamos em …, por meio de …, resultando em …". Os conectores são fixos; as partes, da organização. */
export const TRIADE_PARTS = [
  { key: 'believe', lead: 'Acreditamos em', hint: 'Nosso propósito, a crença que nos move.',
    placeholder: 'Uma educação que transforma vidas e amplia possibilidades.' },
  { key: 'through', lead: 'Por meio de', hint: 'Como entregamos valor, o que fazemos de diferente.',
    placeholder: 'Uma formação integral e personalizada, com professores inspiradores e tecnologia.' },
  { key: 'result', lead: 'Resultando em', hint: 'O impacto que geramos, o que queremos alcançar.',
    placeholder: 'Alunos confiantes, preparados para os desafios do futuro.' },
] as const;
export type TriadePart = (typeof TRIADE_PARTS)[number]['key'];
export const triadeSchema = z.object({
  believe: str(240),
  through: str(240),
  result:  str(240),
  /** a frase-síntese: a fórmula monta; a organização pode reescrever */
  synthesis: str(500),
  /** a frase foi reescrita à mão — deixa de acompanhar as três partes */
  synthesisEdited: z.boolean().default(false),
});
export type Triade = z.infer<typeof triadeSchema>;

/* ── 04 · Visão ───────────────────────────────────────────────────── */
/** Os insumos da visão de futuro; a IA escreve a versão finalizada a partir deles e do propósito. */
export const VISAO_FIELDS = [
  { key: 'arrive',     label: 'Onde queremos chegar?',          placeholder: 'Descreva onde a organização quer chegar no futuro…' },
  { key: 'become',     label: 'O que queremos nos tornar?',     placeholder: 'Descreva o que a organização deseja se tornar…' },
  { key: 'recognized', label: 'Como queremos ser reconhecidos?', placeholder: 'Descreva como a organização quer ser reconhecida pelo mercado, pela sociedade e pelos seus públicos…' },
  { key: 'impact',     label: 'Impacto esperado',               placeholder: 'Descreva o impacto que desejamos gerar no futuro…' },
] as const;
export type VisaoField = (typeof VISAO_FIELDS)[number]['key'];
export const visaoSchema = z.object({
  /** o propósito da organização (vai para o DNA) */
  purpose:     str(300),
  horizonYear: z.number().int().min(2020).max(2100).nullable().default(null),
  arrive:      str(),
  become:      str(),
  recognized:  str(),
  impact:      str(),
  /** a visão finalizada: escrita pela IA a partir dos insumos, editável */
  vision:      str(600),
  /** a organização reescreveu a visão: a IA não a substitui mais (ausente em projetos antigos) */
  visionEdited: z.boolean().optional(),
});
export type Visao = z.infer<typeof visaoSchema>;

/* ── 05 · Valores ─────────────────────────────────────────────────── */
/** Uma versão de um valor: o nome, o detalhamento, quando e quem escreveu. */
export const valorVersionSchema = z.object({
  name:    str(60),
  meaning: str(240),
  at:      z.string().max(40).default(''),
  by:      str(80),
  /** o detalhamento veio da sugestão da IA */
  ia:      z.boolean().default(false),
});
export type ValorVersion = z.infer<typeof valorVersionSchema>;
export const valorSchema = z.object({
  /** identidade estável: o histórico se liga a ela */
  id:      z.string().regex(/^[a-z0-9][a-z0-9-]{1,47}$/).optional(),
  name:    str(60),
  meaning: str(240),
  /** quem e quando escreveu a versão atual (o servidor preenche) */
  at:      z.string().max(40).default(''),
  by:      str(80),
  ia:      z.boolean().default(false),
  /** a sessão de edição (aberta na caneta): mudanças da mesma sessão são uma edição só */
  sess:    z.string().max(24).default(''),
  /** as últimas cinco versões anteriores, da mais recente à mais antiga (o servidor mantém) */
  history: z.array(valorVersionSchema).max(5).default([]),
});
export type Valor = z.infer<typeof valorSchema>;
export const valoresSchema = z.object({
  values: z.array(valorSchema).max(12).default([]),
});
export type Valores = z.infer<typeof valoresSchema>;

/** Ids estáveis ("val-1"…), sem repetição. */
export function withValorIds(c: Valores): Valores {
  const used = new Set<string>();
  let n = 0;
  return {
    values: c.values.map(v => {
      let id = v.id && !used.has(v.id) ? v.id : '';
      while (!id || used.has(id)) id = `val-${++n}`;
      used.add(id);
      return { ...v, id };
    }),
  };
}

/* ── 06 · Business Model Canvas ───────────────────────────────────── */
export const BMC_BLOCKS = [
  ['partners', 'Parcerias-chave'], ['activities', 'Atividades-chave'], ['resources', 'Recursos-chave'],
  ['valueProp', 'Proposta de valor'], ['relationship', 'Relacionamento'], ['channels', 'Canais'],
  ['segments', 'Segmentos de clientes'], ['costs', 'Estrutura de custos'], ['revenue', 'Fontes de receita'],
] as const;
export type BmcBlock = (typeof BMC_BLOCKS)[number][0];
/** Os blocos que são listas de itens (todos, menos a proposta de valor, que é um texto). */
export type BmcListBlock = Exclude<BmcBlock, 'valueProp'>;
export const BMC_LIST_BLOCKS = BMC_BLOCKS.map(([k]) => k).filter((k): k is BmcListBlock => k !== 'valueProp');
/** prefixo dos ids de cada bloco: "seg-1", "can-2"… */
export const BMC_PREFIX: Record<BmcListBlock, string> = {
  partners: 'par', activities: 'atv', resources: 'rcs', relationship: 'rel', channels: 'can', segments: 'seg', costs: 'cst', revenue: 'rct',
};
export const BMC_ITEM_ID = /^[a-z0-9][a-z0-9-]{1,47}$/;

/** Textos antigos ("Visitas, indicação e presença digital.") viram itens. */
export function splitBmcText(s: string): string[] {
  const parts = s.split(/\s*(?:\n|;|•)\s*/).flatMap(p => {
    if (!p.includes(', ')) return [p];
    const xs = p.split(/,\s+/);
    const last = xs.pop()!;
    return [...xs, ...last.split(/\s+e\s+/)];
  });
  return parts.map(p => p.trim().replace(/[.;\s]+$/, '')).filter(Boolean)
    .map(p => p.charAt(0).toUpperCase() + p.slice(1)).slice(0, 16);
}

export const bmcItemSchema = z.object({
  /** identidade estável: as relações se ligam a ela */
  id: z.string().regex(BMC_ITEM_ID).optional(),
  text: str(200),
  /** ids de itens de outros blocos com que este se relaciona (a relação vale nos dois sentidos) */
  links: z.array(z.string().max(48)).max(24).default([]),
  /** sugerido pela IA e ainda não mexido pela organização */
  ai: z.boolean().default(false),
});
export type BmcItem = z.infer<typeof bmcItemSchema>;
const bmcList = z.preprocess(
  v => (typeof v === 'string' ? splitBmcText(v).map(text => ({ text })) : v),
  z.array(bmcItemSchema).max(16).default([]),
);
export const bmcSchema = z.object({
  valueProp:    str(600),
  partners:     bmcList,
  activities:   bmcList,
  resources:    bmcList,
  relationship: bmcList,
  channels:     bmcList,
  segments:     bmcList,
  costs:        bmcList,
  revenue:      bmcList,
});
export type Bmc = z.infer<typeof bmcSchema>;

/* ── 07 · 10 Tipos de Inovação (Doblin) ───────────────────────────── */
export const INNOVATION_TYPES = [
  ['profit', 'Modelo de lucro'], ['network', 'Rede'], ['structure', 'Estrutura'], ['process', 'Processo'],
  ['performance', 'Desempenho do produto'], ['system', 'Sistema de produto'], ['service', 'Serviços'],
  ['channel', 'Canal'], ['brand', 'Marca'], ['engagement', 'Engajamento do cliente'],
] as const;
export type InnovationType = (typeof INNOVATION_TYPES)[number][0];
export const INNOVATION_KEYS = INNOVATION_TYPES.map(([k]) => k) as [InnovationType, ...InnovationType[]];
export const POTENTIALS = ['alto', 'medio', 'baixo'] as const;
export type Potential = (typeof POTENTIALS)[number];
const nivel = z.number().int().min(0).max(5).default(0);
export const innovationInsightSchema = z.object({
  id:        z.string().regex(/^[a-z0-9][a-z0-9-]{1,47}$/).optional(),
  type:      z.enum(INNOVATION_KEYS),
  text:      str(400),
  potential: z.enum(POTENTIALS).default('medio'),
  /** gerado pela IA e ainda não mexido pela organização */
  ai:        z.boolean().default(false),
});
export type InnovationInsight = z.infer<typeof innovationInsightSchema>;
export const inovacaoSchema = z.object({
  types: z.array(z.object({
    key:     z.enum(INNOVATION_KEYS),
    /** maturidade atual e desejada, de 1 a 5 (0 = sem avaliação) */
    current: nivel,
    target:  nivel,
    /** potencial 0–100, derivado da distância e da ambição (projetos antigos: digitado) */
    score:   z.number().int().min(0).max(100).default(0),
    note:    str(),
  })).max(10).default([]),
  insights: z.array(innovationInsightSchema).max(60).default([]),
  /** as rodadas de insights da IA que já entraram na lista (para não entrarem de novo) */
  batches: z.array(z.string().max(40)).max(80).default([]),
});
export type Inovacao = z.infer<typeof inovacaoSchema>;

/* ── 08 · Macro Cenário (matriz: forças, fraquezas, oportunidades e ameaças × PESTAL) ── */
export const MACRO_DIMENSIONS = [
  ['politico', 'Político'], ['economico', 'Econômico'], ['social', 'Social'],
  ['ambiental', 'Ambiental'], ['tecnologico', 'Tecnológico'], ['legal', 'Legal'],
] as const;
export type MacroDimension = (typeof MACRO_DIMENSIONS)[number][0];
export const MACRO_DIMENSION_KEYS = MACRO_DIMENSIONS.map(([k]) => k) as [MacroDimension, ...MacroDimension[]];
export const MACRO_QUADRANTS = [
  ['forca', 'Forças'], ['fraqueza', 'Fraquezas'], ['oportunidade', 'Oportunidades'], ['ameaca', 'Ameaças'],
] as const;
export type MacroQuadrant = (typeof MACRO_QUADRANTS)[number][0];
export const MACRO_QUADRANT_KEYS = MACRO_QUADRANTS.map(([k]) => k) as [MacroQuadrant, ...MacroQuadrant[]];
/** quantos fatores cabem numa célula da matriz */
export const MACRO_PER_CELL = 4;

/** palavras de uma tendência que pesa contra (o formato antigo não dizia se era oportunidade ou ameaça) */
const MACRO_THREAT = /press[aã]o|risco|amea[cç]|instabil|queda|crise|exig|concorr|redu[cç]|perda|escassez|inadimpl|evas[aã]o|restri|encarec|retra[cç]/i;
/**
 * O formato antigo — uma lista de tendências por dimensão, com impacto e
 * probabilidade — vira fatores externos da matriz: ameaça quando o texto
 * pesa contra, oportunidade nos demais.
 */
function legacyMacro(v: unknown) {
  if (!v || typeof v !== 'object' || 'factors' in v) return v;
  const forces = (v as { forces?: unknown }).forces;
  if (!Array.isArray(forces)) return v;
  return {
    factors: forces
      .filter((f): f is { dimension: string; trend: string } => !!f && typeof f === 'object' && typeof f.trend === 'string' && f.trend.trim() !== ''
        && (MACRO_DIMENSION_KEYS as readonly string[]).includes(f.dimension))
      .map(f => ({ quadrant: MACRO_THREAT.test(f.trend) ? 'ameaca' : 'oportunidade', dimension: f.dimension, text: f.trend })),
  };
}
export const macroFactorSchema = z.object({
  /** identidade estável: a seleção na tela e a atualização da demonstração se ligam a ela */
  id:        z.string().regex(/^[a-z0-9][a-z0-9-]{1,47}$/).optional(),
  quadrant:  z.enum(MACRO_QUADRANT_KEYS),
  dimension: z.enum(MACRO_DIMENSION_KEYS),
  text:      str(1000),
  /** sugerido pela IA e ainda não mexido pela organização */
  ai:        z.boolean().default(false),
});
export type MacroFactor = z.infer<typeof macroFactorSchema>;
export const macroSchema = z.preprocess(legacyMacro, z.object({
  factors: z.array(macroFactorSchema).max(MACRO_PER_CELL * 24).default([]),
}));
export type Macro = z.infer<typeof macroSchema>;

/* ── 09 · 3 Horizontes de Inovação ────────────────────────────────── */
export const HORIZON_KEYS = ['h1', 'h2', 'h3'] as const;
export type HorizonKey = (typeof HORIZON_KEYS)[number];
export const horizonInitiativeSchema = z.object({
  /** identidade estável: as evidências e o ponto no gráfico se ligam a ela */
  id:          z.string().regex(/^[a-z0-9][a-z0-9-]{1,47}$/).optional(),
  name:        str(120),
  description: str(600),
  /** sugerida pela IA e ainda não mexida pela organização */
  ai:          z.boolean().default(false),
});
export type HorizonInitiative = z.infer<typeof horizonInitiativeSchema>;
/* projetos antigos: cada iniciativa era só um texto */
const horizonInitiatives = z.preprocess(
  v => (Array.isArray(v) ? v.map(x => (typeof x === 'string' ? { name: x } : x)) : v),
  z.array(horizonInitiativeSchema).max(12).default([]),
);
const horizon = z.object({ initiatives: horizonInitiatives });
export const horizontesSchema = z.object({
  h1: horizon.default({ initiatives: [] }),
  h2: horizon.default({ initiatives: [] }),
  h3: horizon.default({ initiatives: [] }),
});
export type Horizontes = z.infer<typeof horizontesSchema>;

/* ── 10 · 5W2H (Plano de Ação) ───────────────────────────────────── */
export const PRIORIDADES = ['alta', 'media', 'baixa'] as const;
export type Prioridade = (typeof PRIORIDADES)[number];
export const w5h2ActionSchema = z.object({
  /** identidade estável: a linha na tela e a ordem dentro da mesma prioridade */
  id:       z.string().regex(/^[a-z0-9][a-z0-9-]{1,47}$/).optional(),
  what:     str(240),
  why:      str(240),
  where:    str(120),
  who:      str(120),
  when:     z.union([z.iso.date(), z.literal('')]).default(''),
  how:      str(240),
  howMuch:  z.number().nonnegative().nullable().default(null),
  priority: z.enum(PRIORIDADES).default('media'),
  /** proposta pela IA (o quê e a prioridade) e ainda não reescrita pela organização */
  ai:       z.boolean().default(false),
  /** de onde a IA tirou a ação (ex.: "H1 · Jornada de matrícula sem papel") */
  origin:   str(120),
  /** quando foi concluída (vazio: ainda aberta) — o Pulse conta a evolução por aqui */
  doneAt:   z.union([z.iso.date(), z.literal('')]).default(''),
});
export type W5h2Action = z.infer<typeof w5h2ActionSchema>;
export const w5h2Schema = z.object({
  actions: z.array(w5h2ActionSchema).max(60).default([]),
  /** quando a IA propôs as ações iniciais (nulo: ainda não propôs — a tela pede na primeira visita) */
  aiAt: z.string().max(40).nullable().default(null),
});
export type W5h2 = z.infer<typeof w5h2Schema>;

/* ── o catálogo ───────────────────────────────────────────────────── */
export const STRATEGY_SCHEMAS = {
  gaps:        gapsSchema,
  diferenciais: diferenciaisSchema,
  triade:      triadeSchema,
  visao:       visaoSchema,
  valores:     valoresSchema,
  bmc:         bmcSchema,
  inovacao:    inovacaoSchema,
  macro:       macroSchema,
  horizontes:  horizontesSchema,
  w5h2:        w5h2Schema,
} as const;

export type StrategyToolKey = keyof typeof STRATEGY_SCHEMAS;
export type StrategyContent<K extends StrategyToolKey> = z.infer<(typeof STRATEGY_SCHEMAS)[K]>;
export const STRATEGY_TOOL_KEYS = Object.keys(STRATEGY_SCHEMAS) as StrategyToolKey[];
export const strategyToolKeySchema = z.enum(STRATEGY_TOOL_KEYS as [StrategyToolKey, ...StrategyToolKey[]]);

export type StrategyCategory = 'diagnostico' | 'posicionamento' | 'inovacao';
export const STRATEGY_CATEGORIES: ReadonlyArray<[StrategyCategory, string]> = [
  ['diagnostico', 'Diagnóstico'], ['posicionamento', 'Posicionamento'], ['inovacao', 'Inovação'],
];

export interface StrategyToolMeta {
  key: StrategyToolKey;
  no: string;
  name: string;
  desc: string;
  category: StrategyCategory;
}

export const STRATEGY_TOOLS: readonly StrategyToolMeta[] = [
  { key: 'gaps',         no: '01', name: 'Mapa de Gaps',              desc: 'Identifique fragilidades e oportunidades.',     category: 'diagnostico' },
  { key: 'diferenciais', no: '02', name: 'Diferenciais Competitivos', desc: 'Identifique o que torna sua organização única.', category: 'posicionamento' },
  { key: 'triade',       no: '03', name: 'Tríade de Valor',           desc: 'Defina o valor que você gera.',                  category: 'posicionamento' },
  { key: 'visao',        no: '04', name: 'Visão',                     desc: 'Construa a visão de futuro.',                    category: 'posicionamento' },
  { key: 'valores',      no: '05', name: 'Valores',                   desc: 'Estabeleça os princípios que guiam.',            category: 'posicionamento' },
  { key: 'bmc',          no: '06', name: 'Business Model Canvas',     desc: 'Estruture seu modelo de negócio.',               category: 'diagnostico' },
  { key: 'inovacao',     no: '07', name: '10 Tipos de Inovação',      desc: 'Explore oportunidades de inovação.',             category: 'inovacao' },
  { key: 'macro',        no: '08', name: 'Macro Cenário',             desc: 'Analise o contexto e tendências.',               category: 'diagnostico' },
  { key: 'horizontes',   no: '09', name: '3 Horizontes de Inovação',  desc: 'Organize o presente, o próximo e o futuro.',     category: 'inovacao' },
  { key: 'w5h2',         no: '10', name: '5W2H',                      desc: 'Transforme estratégia em ações.',                category: 'inovacao' },
];

export const strategyTool = (key: StrategyToolKey) => STRATEGY_TOOLS.find(t => t.key === key)!;

export const emptyStrategy = <K extends StrategyToolKey>(key: K): StrategyContent<K> =>
  STRATEGY_SCHEMAS[key].parse({}) as StrategyContent<K>;

/** Progresso de 0 a 1, derivado do conteúdo. */
export function strategyProgress<K extends StrategyToolKey>(key: K, content: StrategyContent<K>): number {
  switch (key) {
    case 'gaps': {
      const c = content as Gaps;
      const all = c.factors.flatMap(f => f.indicators);
      return all.length ? all.reduce((s, i) => s + gapIndicatorProgress(i), 0) / all.length : 0;
    }
    case 'diferenciais': {
      const c = content as Diferenciais;
      const ok = c.items.filter(i => filled(i.label) && diffRated(i)).length;
      return ratio(ok, 3);
    }
    case 'triade': {
      const c = content as Triade;
      return ratio([c.believe, c.through, c.result].filter(filled).length, 3);
    }
    case 'visao': {
      /* projetos antigos (só propósito e visão) continuam completos */
      const c = content as Visao;
      const inputs = [c.arrive, c.become, c.recognized, c.impact].filter(filled).length;
      if (!inputs) return ratio([c.purpose, c.vision].filter(filled).length + (c.horizonYear ? 1 : 0), 3);
      return ratio(inputs + (c.horizonYear ? 1 : 0) + (filled(c.vision) ? 1 : 0), 6);
    }
    case 'valores': {
      const c = content as Valores;
      return ratio(c.values.filter(v => filled(v.name) && filled(v.meaning)).length, 4);
    }
    case 'bmc': {
      const c = content as Bmc;
      const blocks = BMC_LIST_BLOCKS.filter(k => c[k].some(i => filled(i.text))).length + (filled(c.valueProp) ? 1 : 0);
      return ratio(blocks, BMC_BLOCKS.length);
    }
    case 'inovacao': {
      /* o diagnóstico pesa 80%; os insights, 20% (três já bastam) */
      const c = content as Inovacao;
      const diag = c.types.filter(t => (t.current > 0 && t.target > 0) || (!t.current && !t.target && t.score > 0)).length;
      const ins = c.insights.filter(i => filled(i.text)).length;
      return ratio(diag, INNOVATION_TYPES.length) * .8 + ratio(ins, 3) * .2;
    }
    case 'macro': {
      /* os quatro quadrantes pesam mais que a amplitude das dimensões */
      const fs = (content as Macro).factors.filter(f => filled(f.text));
      return ratio(new Set(fs.map(f => f.quadrant)).size, MACRO_QUADRANTS.length) * .6 + ratio(new Set(fs.map(f => f.dimension)).size, MACRO_DIMENSIONS.length) * .4;
    }
    case 'horizontes': {
      /* os três horizontes com iniciativa pesam mais; descrever cada uma completa */
      const c = content as Horizontes;
      const named = HORIZON_KEYS.map(k => c[k].initiatives.filter(i => filled(i.name)));
      const described = named.flat().filter(i => filled(i.description)).length;
      return ratio(named.filter(xs => xs.length).length, 3) * .7 + ratio(described, 6) * .3;
    }
    case 'w5h2': {
      /* linha sem "o quê" não conta; cada ação vale pelos sete campos preenchidos */
      const acts = (content as W5h2).actions.filter(a => filled(a.what));
      if (!acts.length) return 0;
      const per = acts.map(a => ratio(
        [a.what, a.why, a.where, a.who, a.when, a.how].filter(filled).length + (a.howMuch != null ? 1 : 0), 7));
      return Math.min(1, (per.reduce((s, v) => s + v, 0) / per.length) * ratio(acts.length, 5));
    }
  }
  return 0;
}

export type ToolState = 'todo' | 'now' | 'done';
export const toolState = (progress: number): ToolState =>
  progress >= .999 ? 'done' : progress > 0 ? 'now' : 'todo';
